import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';

// Custom endpoint with multiple optional query params — keep manual
export function useInvoices(loanId?: string, status?: string, type?: string) {
  return useQuery({
    queryKey: ['invoices', loanId, status, type],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (loanId) params.append('loanId', loanId);
      if (status) params.append('status', status);
      if (type) params.append('type', type);
      const queryStr = params.toString();
      const { data } = await api.get(`/invoices${queryStr ? `?${queryStr}` : ''}`);
      return data;
    },
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const { data } = await api.get(`/invoices/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: result } = await api.post('/invoices', data);
      return result;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// Custom action endpoint /issue — keep manual
export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/invoices/${id}/issue`);
      return data;
    },
    onSuccess: result => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      if (result?.loanId) invalidateLoanActivity(qc, result.loanId);
    },
  });
}

// Custom action endpoint /payment — keep manual
export function useRecordInvoicePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const { data: result } = await api.post(`/invoices/${id}/payment`, data);
      return result;
    },
    onSuccess: result => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      if (result?.loanId) invalidateLoanActivity(qc, result.loanId);
    },
  });
}

/** @deprecated Use useRecordInvoicePayment instead — renamed to avoid collision with use-payments.ts */
export const useRecordPayment = useRecordInvoicePayment;

// Custom action endpoint /cancel — keep manual
export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/invoices/${id}/cancel`);
      return data;
    },
    onSuccess: result => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      if (result?.loanId) invalidateLoanActivity(qc, result.loanId);
    },
  });
}

export function useInvoiceSummary(loanId: string) {
  return useQuery({
    queryKey: ['invoice-summary', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/invoices/summary/${loanId}`);
      return data;
    },
    enabled: !!loanId,
  });
}

export function useOverdueInvoices() {
  return useQuery({
    queryKey: ['overdue-invoices'],
    queryFn: async () => {
      const { data } = await api.get('/invoices/overdue');
      return data;
    },
  });
}
